/**
 * Extract unique speaker names from transcript text.
 * Supports patterns: "Speaker Name: text" and "[Speaker Name]: text"
 */
export function parseSpeakersFromTranscript(text: string): string[] {
	if (!text.trim()) return []
	const seen = new Set<string>()
	const regex = /^(?:\[([^\]]+)\]|([^:\n]+)):\s/gm
	let match = regex.exec(text)
	while (match !== null) {
		const name = (match[1] ?? match[2] ?? '').trim()
		if (name) seen.add(name)
		match = regex.exec(text)
	}
	return Array.from(seen).sort()
}

/**
 * Replace all occurrences of a speaker label with a new name.
 * Handles both "Name: " and "[Name]: " patterns.
 */
export function replaceSpeakerInTranscript(text: string, oldName: string, newName: string): string {
	const escaped = oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	let result = text.replace(new RegExp(`\\[${escaped}\\]:\\s`, 'g'), `[${newName}]: `)
	result = result.replace(new RegExp(`(^|\\n)${escaped}:\\s`, 'gm'), `$1${newName}: `)
	return result
}

export interface TranscriptSegment {
	speaker: string
	text: string
}

const speakerLineRegex = /^(?:\[([^\]]+)\]|([^:\n]+)):\s*(.*)$/

/**
 * Split transcript into segments by speaker labels.
 */
export function segmentTranscript(text: string): TranscriptSegment[] {
	const segments: TranscriptSegment[] = []
	const lines = text.split('\n')
	let currentSpeaker = ''
	let currentLines: string[] = []

	for (const line of lines) {
		const m = line.match(speakerLineRegex)
		if (m) {
			if (currentSpeaker || currentLines.length > 0) {
				segments.push({
					speaker: currentSpeaker,
					text: currentLines.join('\n').trim(),
				})
			}
			currentSpeaker = (m[1] ?? m[2] ?? '').trim()
			currentLines = m[3] ? [m[3]] : []
		} else {
			currentLines.push(line)
		}
	}
	if (currentSpeaker || currentLines.length > 0) {
		segments.push({
			speaker: currentSpeaker,
			text: currentLines.join('\n').trim(),
		})
	}
	if (segments.length === 0 && text.trim()) {
		segments.push({ speaker: '', text: text.trim() })
	}
	return segments
}

/**
 * Filter transcript to only include segments from selected speakers.
 * If selectedSpeakers is empty or includes all parsed speakers, returns original text.
 */
export function filterTranscriptBySpeakers(text: string, selectedSpeakers: Set<string>): string {
	const segments = segmentTranscript(text)
	if (segments.length === 0) return text
	const allSpeakers = new Set(segments.map((s) => s.speaker))
	if (selectedSpeakers.size === 0 || selectedSpeakers.size >= allSpeakers.size) {
		return text
	}
	return segments
		.filter((s) => !s.speaker || selectedSpeakers.has(s.speaker))
		.map((s) => {
			if (s.speaker) return `${s.speaker}: ${s.text}`
			return s.text
		})
		.join('\n\n')
}
